import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Minus, Plus } from 'lucide-react';
import type { Product } from '../types';
import { getDisplayTag } from '../types';
import { OptimizedImage } from '../utils/imageOptimization';

interface ProductPopupProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
  onAddToCart: (product: Product, quantity: number) => void;
}

export function ProductPopup({ product, isOpen, onClose, onAddToCart }: ProductPopupProps) {
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Reset state when product changes
  useEffect(() => {
    if (isOpen) {
      setQuantity(1);
      setCurrentImageIndex(0);
    }
  }, [isOpen, product]);

  if (!isOpen || !product) return null;

  // Use the images array if available, otherwise construct from image and backImage
  const images = product.images && product.images.length > 0 
    ? product.images 
    : [product.image, product.backImage].filter(Boolean) as string[];

  const handleNextImage = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrevImage = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Minimum swipe distance (in pixels)
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && images.length > 1) {
      handleNextImage();
    }
    if (isRightSwipe && images.length > 1) {
      handlePrevImage();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose}>
      <div 
        className="relative w-full max-w-4xl max-h-[90vh] md:max-h-[85vh] bg-[#1a1a1a] border-2 border-[var(--color-sansi-green)] shadow-[8px_8px_0px_var(--color-sansi-orange)] overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-[var(--color-sansi-orange)] transition-colors z-10 bg-black/20 p-2 rounded-full backdrop-blur-md"
        >
          <X size={24} />
        </button>

        <div className="grid md:grid-cols-2 min-h-[400px] flex-1 overflow-hidden">
          {/* Image Section */}
          <div 
            className="bg-white/5 p-8 flex items-center justify-center border-b md:border-b-0 md:border-r border-white/10 relative overflow-hidden group select-none touch-pan-y"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--color-sansi-green)_0%,_transparent_70%)] opacity-5" />
            
            <OptimizedImage 
              src={images[currentImageIndex]} 
              alt={product.name}
              className="w-full h-full object-contain max-h-[350px] transition-transform duration-500 animate-in fade-in zoom-in-50"
              key={currentImageIndex} // Force re-render for animation
            />

            {/* Carousel Controls - Arrows only for multiple images */}
            {images.length > 1 && (
              <>
                <button 
                  onClick={handlePrevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-black/40 hover:bg-[var(--color-sansi-orange)] p-2 rounded-full backdrop-blur-sm transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100 z-20 active:scale-95"
                  aria-label="Previous image"
                >
                  <ChevronLeft size={24} />
                </button>
                <button 
                  onClick={handleNextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-black/40 hover:bg-[var(--color-sansi-orange)] p-2 rounded-full backdrop-blur-sm transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100 z-20 active:scale-95"
                  aria-label="Next image"
                >
                  <ChevronRight size={24} />
                </button>
              </>
            )}
            
            {/* Dots - Always show, dynamic count based on available images */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, idx) => (
                <div 
                  key={idx}
                  className={`w-2 h-2 rounded-full transition-colors ${idx === currentImageIndex ? 'bg-[var(--color-sansi-orange)]' : 'bg-white/20'}`}
                />
              ))}
            </div>

            <div className="absolute top-4 left-4 flex flex-col items-start gap-1">
              {getDisplayTag(product.tag) && (
                <span className="bg-[var(--color-sansi-orange)] text-black text-xs font-black uppercase px-3 py-1">
                  {getDisplayTag(product.tag)}
                </span>
              )}
              {product.offer && (
                <span className="bg-red-600 text-white text-xs font-black uppercase px-3 py-1">
                  {product.offer.discount}% OFF
                </span>
              )}
            </div>
          </div>

          {/* Content Section */}
          <div className="p-8 md:p-10 flex flex-col h-full bg-[#1a1a1a] overflow-y-auto custom-scrollbar">
            {/* Header Section */}
            <div className="flex-shrink-0 space-y-6 mb-6">
              <div>
                <h2 className="text-3xl md:text-4xl font-display font-bold text-white uppercase tracking-wide leading-none mb-3">
                  {product.name}
                </h2>
                {product.weight && (
                  <p className="text-gray-400 text-sm md:text-base mb-3">
                    {product.weight}
                  </p>
                )}
                <div className="flex flex-col gap-1">
                  {product.offer && (
                    <span className="text-gray-400 font-mono text-lg line-through decoration-red-500 decoration-2">
                       Rs. {product.offer.originalPrice.toLocaleString()}
                    </span>
                  )}
                  <p className="text-[var(--color-sansi-green)] font-mono text-xl md:text-2xl font-bold">
                    Rs. {product.price.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Description Section */}
            <div className="flex-shrink-0 space-y-6 mb-6">
              <div className="space-y-6">
                <div className="space-y-2">
                  <h3 className="flex items-center gap-2 text-white/60 font-mono text-xs uppercase tracking-widest">
                    Description
                  </h3>
                  <p className="text-gray-300 leading-relaxed font-light text-lg">
                    {product.description || "Ideally balanced flavor profile for the perfect snack."}
                  </p>
                </div>

                <div className="space-y-2">
                  <h3 className="flex items-center gap-2 text-white/60 font-mono text-xs uppercase tracking-widest">
                    Ingredients
                  </h3>
                  <p className="text-gray-300 leading-relaxed font-light">
                    {product.ingredients || "100% Natural Fruit, No Added Sugar, No Preservatives."}
                  </p>
                </div>
              </div>
            </div>

            {/* Footer Section */}
            <div className="flex-shrink-0 pt-6 mt-6 border-t border-white/10 space-y-4">
              {/* Quantity Selector */}
              <div className="flex items-center justify-between">
                <span className="text-white/60 font-mono text-xs uppercase tracking-widest">Quantity</span>
                <div className="flex items-center border-2 border-white/20 rounded-none overflow-hidden">
                  <button 
                    onClick={() => setQuantity(q => Math.max(1, q - 1))}
                    className="p-3 hover:bg-white/10 text-white transition-colors"
                  >
                    <Minus size={16} />
                  </button>
                  <span className="w-12 text-center font-bold text-lg text-white">{quantity}</span>
                  <button 
                    onClick={() => setQuantity(q => q + 1)}
                    className="p-3 hover:bg-white/10 text-white transition-colors"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>

              <button
                onClick={() => {
                  onAddToCart(product, quantity);
                  onClose();
                }}
                className="w-full bg-white text-black font-black uppercase py-4 text-lg hover:bg-[var(--color-sansi-orange)] hover:text-white transition-all duration-300 border-2 border-transparent hover:border-white shadow-lg hover:shadow-orange-500/20 active:scale-[0.98] flex items-center justify-center gap-2"
              >
                <span>Add to Cart</span>
                <span className="text-sm opacity-60 font-normal normal-case">
                  (Rs. {(product.price * quantity).toLocaleString()})
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
